import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { NewUser, UserRole } from '../types/user';
import { userService } from '../services/userService';

interface AddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const userRoles: UserRole[] = ["OWNER", "MANAGER", "OFFICE_ADMIN", "ENGINEER", "SUPERVISOR", "TECHNICIAN"];

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({ open, onClose, onSave }) => {
  const [newUser, setNewUser] = useState<NewUser>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'TECHNICIAN',
    password: '',
    phone: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // 1. Handler for TextField components (input/textarea elements)
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // The 'name' is guaranteed to be a key of NewUser because of the JSX setup
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  // 2. Handler for Select component (SelectChangeEvent type)
  const handleSelectChange = (e: SelectChangeEvent<UserRole>) => {
    const { name, value } = e.target;
    // The 'name' is 'role' and 'value' is guaranteed to be a UserRole
    setNewUser(prev => ({ ...prev, [name]: value as UserRole }));
  };

  const handleSubmit = async () => {
    if (!newUser.username || !newUser.password || !newUser.email) {
      setError('Username, password, and email are required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await userService.createUser(newUser);
      onSave(); // Trigger refresh on the parent page
      onClose(); // Close the modal
    } catch (err: any) {
      console.error('Failed to create user:', err);
      const errorData = err.response?.data;
      if (errorData) {
        // Concatenate all error messages from the backend
        const messages = Object.keys(errorData).map(key => `${key}: ${errorData[key].join(' ')}`).join('; ');
        setError(messages);
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New Employee</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {/* Use handleTextFieldChange for all TextFields */}
          <TextField margin="normal" required fullWidth label="Username" name="username" value={newUser.username} onChange={handleTextFieldChange} />
          <TextField margin="normal" required fullWidth label="Password" name="password" type="password" value={newUser.password} onChange={handleTextFieldChange} />
          <TextField margin="normal" required fullWidth label="Email Address" name="email" value={newUser.email} onChange={handleTextFieldChange} />
          <TextField margin="normal" fullWidth label="First Name" name="first_name" value={newUser.first_name} onChange={handleTextFieldChange} />
          <TextField margin="normal" fullWidth label="Last Name" name="last_name" value={newUser.last_name} onChange={handleTextFieldChange} />
          <TextField margin="normal" fullWidth label="Phone" name="phone" value={newUser.phone} onChange={handleTextFieldChange} />
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              name="role"
              value={newUser.role}
              label="Role"
              // Use handleSelectChange for the Select component
              onChange={handleSelectChange}
            >
              {userRoles.map(role => (
                <MenuItem key={role} value={role}>{role.replace('_', ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEmployeeModal;